import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import { notification } from 'antd'
import ErrorMessageModal from 'components/Modals/ErrorMessageModal'
import MessageModal from 'components/Modals/MessageModal'
import { RiseUpErrors } from 'constants/errors'
import useErrorMessage from 'hooks/useErrorMessage'
import { useGTMData } from 'hooks/useGTMData'
import { useNorthCapitalApi } from 'hooks/useNorthCapitalAPI'
import usePayments from 'hooks/usePayments'
import { useUTMTracking } from 'hooks/useUTMTracking'
import queryString from 'query-string'
import React, { useEffect, useState } from 'react'
import ReactGA from 'react-ga4'
import { LuLoader2 } from 'react-icons/lu'
import { useLocation, useNavigate } from 'react-router-dom'
import swal from 'sweetalert'
import { getModifiedUrlWithStep } from 'utils'
import { consoleLog } from 'utils/simpleLogger'
import CompanyLogo from '../../assets/afbc.png'
import BankAccountIcon from '../../assets/bank-account.png'
import BankWireIcon from '../../assets/bank-wire.png'
import CreditDebitIcon from '../../assets/credit.png'
import Side2 from '../../assets/investing-signup-images/refilling-beer.jpg'
import Button from '../../components/Button'
import Stepper from '../../components/Stepper'
import { useRiseUpAPIV1 } from '../../hooks/useRiseUpAPIV1'
import { IFormProps, InvestingPersonEnum } from '../../interfaces'

export enum PaymentMethodsEnum {
  ACH = 'ACH',
  CARD = 'CARD',
  WIRE = 'WIRE',
}

const PaymentSelection = (props: IFormProps) => {
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [existingCreditCard, setExistingCreditCard] = useState(false)
  const [selected, setSelected] = useState<PaymentMethodsEnum | null>(
    props.getValues('investingProfile.paymentMethod') in PaymentMethodsEnum
      ? (props.getValues('investingProfile.paymentMethod') as PaymentMethodsEnum)
      : null,
  )
  const [accountId, setAccountId] = useState('')
  const [investorId, setInvestorId] = useState('')
  const [secondaryAccountId, setSecondaryAccountId] = useState('')
  const [secondaryInvestorId, setSecondaryInvestorId] = useState('')
  const { getOnboardProgress, updateOnboardProgress, finalizeUserCreation } = useRiseUpAPIV1()
  const { errorMessage, setErrorMessage } = useErrorMessage()
  const {
    onboardIndividual,
    onboardJoint,
    onboardEntity,
    createTrade,
    addCard,
    linkExternalAccount,
    getLinkedCreditCard,
    getNorthCapitalIds,
  } = useNorthCapitalApi()
  const { initiateCCPayment } = usePayments()
  const sendDataToGTM = useGTMDispatch()
  const gtmData = useGTMData(props.getValues)
  const {
    getFormattedUTMParams, // Helper to generate a complete GTM event object
  } = useUTMTracking()
  const navigate = useNavigate()
  const location = useLocation()

  const getFreshBearerToken = async () => {
    const getOnboardProgressRes = await getOnboardProgress(props.getValues('onboardingId'))
    if (getOnboardProgressRes.status === 200) {
      return getOnboardProgressRes.data.accessToken
    }
  }

  const getUserNorthCapitalIds = async (accessToken: string) => {
    const result = await getNorthCapitalIds(accessToken)

    if (result.data.data) {
      return result.data.data.northCapitalIds
    }

    return {
      accountId: null,
    }
  }

  const handlePaymentSelection = (value: PaymentMethodsEnum) => {
    setSelected(value)
    props.setPaymentOptionSelected(value)
    props.setValue('investingProfile.paymentMethod', value)
  }

  const getValuesAndData = () => {
    const values = props.getValues()
    const data = { ...values, lastCompletedStage: '8' }

    return { values, data }
  }

  const onProceed = async () => {
    setIsLoading(true)
    const { data } = getValuesAndData()
    const updateSuccess = await handleUpdateOnboardingProgress(data)
    const pathname = location.pathname
    const currentParams = queryString.parse(location.search)
    const updatedUrl = getModifiedUrlWithStep(pathname, currentParams, 'payments')

    if (updateSuccess === 'success') {
      navigate(updatedUrl)
      props.nextStep('9')
    }
  }

  const onError = async (error: any) => {
    console.log(error)
    ReactGA.event({
      category: 'Payment selection page',
      action: `Payment selection page error`,
      label: JSON.stringify(error),
    })
    setIsLoading(false)
    setError(error.response.data._error || 'Unexpected Error')
  }

  const handleCCPaymentMethod = async (accessToken: string) => {
    let getLinkedCreditCardRes = await getLinkedCreditCard(accessToken)

    const storedAccountId = props.getValues('investingProfile.accountId') || accountId
    const storedInvestorId = props.getValues('investingProfile.investorId') || investorId

    if (getLinkedCreditCardRes.data.statusDesc === 'No Credit Card Linked') {
      let addCardRes = await addCard(storedAccountId, storedInvestorId, accessToken)
      consoleLog(addCardRes)

      props.setValue('paymentIframe', addCardRes.data.accountDetails)
      onProceed()
    } else {
      props.setValue('investingProfile.accountId', getLinkedCreditCardRes.data.creditcardDetails.accountId as string)
      setAccountId(getLinkedCreditCardRes.data.creditcardDetails.accountId as string)

      const paymentRes = await initiateCCPayment(
        accessToken,
        getLinkedCreditCardRes.data.creditcardDetails.accountId,
        props.getValues('investingProfile.totalShares'),
      )

      if (paymentRes.data.statusCode === '101') {
        const res = await finalizeUserCreation(props.getValues('onboardingId'))
        consoleLog(res)
        const pathname = location.pathname
        const currentParams = queryString.parse(location.search)
        const updatedUrl = getModifiedUrlWithStep(pathname, currentParams, 'success_page')
        navigate(updatedUrl)
        props.nextStep('Success')
      } else {
        throw new Error('Payment initiation failed with status code: ' + paymentRes.data.statusCode)
      }
    }
  }

  const handleACHPaymentMethod = async (accessToken: string) => {
    const res = await linkExternalAccount(accessToken)
    props.setValue('paymentIframe', res.data.accountDetails)
    onProceed()
  }

  const handleWirePaymentMethod = async (accessToken: string) => {
    consoleLog('Creating trade...')
    const tradeRes = await createTrade(
      props.getValues('investingProfile.accountId'),
      'WIRE',
      props.getValues('investingProfile.totalShares'),
      accessToken,
    )

    const { data } = getValuesAndData()
    const updateSuccess = await handleUpdateOnboardingProgress(data)
    if (updateSuccess === 'success') {
      const tradeId = tradeRes.data.purchaseDetails[1][0].tradeId
      props.setValue('investingProfile.tradeId', tradeId)

      onProceed()
    }
  }
  const handlePaymentMethod = async (accessToken: string) => {
    consoleLog('Initializing iframe link')

    const { data } = getValuesAndData()
    await handleUpdateOnboardingProgress(data)

    const paymentMethod = props.getValues('investingProfile.paymentMethod')
    if (paymentMethod === PaymentMethodsEnum.WIRE) {
      await handleWirePaymentMethod(accessToken)
    } else if (paymentMethod === PaymentMethodsEnum.CARD) {
      await handleCCPaymentMethod(accessToken)
    } else if (paymentMethod === PaymentMethodsEnum.ACH) {
      await handleACHPaymentMethod(accessToken)
    }
  }

  const handleOnboardingProcess = async (accessToken: string) => {
    const { values, data } = getValuesAndData()
    const formattedUTMParams = getFormattedUTMParams()
    const northCapitalIds = await getUserNorthCapitalIds(accessToken)

    sendDataToGTM({
      event: 'add_payment_info',
      step_title: 'PAYMENT_SELECTION',
      ...gtmData,
      ...formattedUTMParams,
    })

    const storedAccountId = props.getValues('investingProfile.accountId') || northCapitalIds.accountId

    if (!storedAccountId) {
      consoleLog('Onboarding...')
      const onboardRes =
        props.getValues('investingProfile.option') === InvestingPersonEnum.FOR_MYSELF
          ? await onboardIndividual(values, accessToken)
          : props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY
            ? await onboardJoint(values, accessToken)
            : await onboardEntity(values, accessToken)

      if (props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY) {
        //primary
        props.setValue(
          'investingProfile.accountId',
          onboardRes.data.primary.fintechResourceIds.northCapital.accountId as string,
        )
        setAccountId(onboardRes.data.primary.fintechResourceIds.northCapital.accountId as string)
        props.setValue(
          'investingProfile.investorId',
          onboardRes.data.primary.fintechResourceIds.northCapital.partyId ||
            onboardRes.data.primary.fintechResourceIds.northCapital.entityId,
        )
        setInvestorId(
          onboardRes.data.primary.fintechResourceIds.northCapital.partyId ||
            onboardRes.data.primary.fintechResourceIds.northCapital.entityId,
        )
        // secondary
        props.setValue(
          'investingProfile.secondaryAccountId',
          onboardRes.data.secondary.fintechResourceIds.northCapital.accountId as string,
        )
        setSecondaryAccountId(onboardRes.data.secondary.fintechResourceIds.northCapital.accountId as string)
        props.setValue(
          'investingProfile.secondaryInvestorId',
          onboardRes.data.secondary.fintechResourceIds.northCapital.partyId ||
            onboardRes.data.secondary.fintechResourceIds.northCapital.entityId,
        )
        setSecondaryInvestorId(
          onboardRes.data.secondary.fintechResourceIds.northCapital.partyId ||
            onboardRes.data.secondary.fintechResourceIds.northCapital.entityId,
        )
      } else {
        props.setValue(
          'investingProfile.accountId',
          onboardRes.data.fintechResourceIds.northCapital.accountId as string,
        )
        setAccountId(onboardRes.data.fintechResourceIds.northCapital.accountId as string)
        props.setValue(
          'investingProfile.investorId',
          onboardRes.data.fintechResourceIds.northCapital.partyId ||
            onboardRes.data.fintechResourceIds.northCapital.entityId,
        )
        setInvestorId(
          onboardRes.data.fintechResourceIds.northCapital.partyId ||
            onboardRes.data.fintechResourceIds.northCapital.entityId,
        )
      }
    }
    // setIsLoading(false)
    await handlePaymentMethod(accessToken)
  }

  const handleSubmit = async () => {
    if (!props.getValues('investingProfile.paymentMethod')) {
      notification.warning({
        duration: 2,
        message: 'Payment method is required',
        placement: 'topRight',
        description: 'Select a payment method.',
        key: 'form-notif',
      })
      return
    }

    props.setPaymentOptionSelected(props.getValues('investingProfile.paymentMethod'))
    setIsLoading(true)

    const accessToken = await getFreshBearerToken()

    try {
      await handleOnboardingProcess(accessToken)
    } catch (error: any) {
      const { data } = getValuesAndData()
      // Saving the account id in case it's already created when the error happens
      await handleUpdateOnboardingProgress(data)
      console.log(error)
      const errorResponse = (error as any)?.response?.data
      const errorMessage = (errorResponse?._error as string) || (errorResponse?.message as string)
      console.log('errorMessage', errorMessage)
      if (errorMessage) {
        const userAlreadyRegistered =
          errorMessage === 'User is already registered in North Capital system.' ||
          errorMessage.includes('User is already registered')

        if (userAlreadyRegistered) {
          await handlePaymentMethod(accessToken)
        } else {
          onError(error)
        }
      } else {
        console.log('errorResponse', errorResponse)
        onError(error)
      }
    }
  }

  const handleUpdateOnboardingProgress = async (data: any): Promise<string> => {
    try {
      const updateOnboardProgressRes = await updateOnboardProgress(data)

      if (updateOnboardProgressRes.status === 201 || updateOnboardProgressRes.status === 200) {
        sendDataToGTM({
          event: 'add_payment_info',
          step_title: 'PAYMENT_SELECTION',
          ...gtmData,
        })
        return 'success'
      }
      return 'success'
    } catch (error: any) {
      // catch the duplicate errors
      if (error.response.data === RiseUpErrors.DuplicatePhoneNumber) {
        setErrorMessage(RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.')
        return 'error'
      }
      if (error.response.data === RiseUpErrors.DuplicateSSN) {
        setErrorMessage(RiseUpErrors.DuplicateSSN + ' Please update the ssn.')
        return 'error'
      }
      swal({
        title: 'Error!',
        text: 'Please check if your details are valid and correct. If error persists, contact us.',
        icon: 'error',
      })
      return 'error'
    }
  }

  useEffect(() => {
    props.setCurrentStage(3)
    props.setValue('paymentIframe', '')
  }, []) // eslint-disable-line

  // useEffect(() => {
  //   sendDataToGTM({
  //     event: 'add_payment_info',
  //     step_title: 'PAYMENT_SELECTION',
  //     ...gtmData,
  //   })
  // }, [])

  return (
    <div className="flex h-screen w-full flex-col md:flex-row md:justify-start">
      <div className="mt-10 flex flex-col sm:w-screen md:mt-0 md:min-h-screen md:w-[45%] lg:w-[40%] xl:w-[35%]">
        <div className="relative w-screen px-10 sm:h-auto sm:px-16 md:hidden">
          <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
        </div>
        <div className="relative hidden flex-grow md:flex">
          <img className="flex h-screen w-full" src={Side2} alt={`${Side2}`} />
        </div>
      </div>
      <div className="flex flex-col p-10 sm:w-[100%] md:w-[55%] lg:h-screen lg:w-[60%] lg:overflow-y-scroll xl:w-[65%]">
        {/* <div className="justify-center my-10 hidden md:flex">
          <div className="w-1/2">
            <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
          </div>
        </div> */}

        <div className="md:my-3">
          <Stepper current={props.currentStage} />
        </div>

        <div className="py-8 text-left text-2xl font-semibold text-gray-900 md:py-12 md:pb-10 md:text-4xl">
          How will you pay for your investment?
        </div>

        <div className="grid gap-8 px-12 py-6 md:flex md:px-0 md:py-0">
          <div
            style={{ border: '2px solid #E5E7EB' }}
            className={`flex min-h-[174px] flex-col items-center justify-center rounded-md border-[#E5E7EB] p-7 text-center hover:cursor-pointer sm:border-solid sm:border-[#DDD] md:min-h-[252px] md:w-1/4 md:border-2 ${
              selected === PaymentMethodsEnum.ACH ? 'border-[#1A56DB] bg-[#1A56DB] text-[#FFFFFF] shadow-xl' : ''
            }`}
            onClick={() => {
              if (!isLoading) {
                handlePaymentSelection(PaymentMethodsEnum.ACH)
              }
            }}
          >
            <img src={BankAccountIcon} alt="BankAccountIcon" className="min-w-[92px]" />
            <div className={`mt-4 text-base ${selected === PaymentMethodsEnum.ACH ? 'font-bold' : 'font-normal'}`}>
              From Bank Account
            </div>
          </div>

          <div
            style={{ border: '2px solid #E5E7EB' }}
            className={`flex min-h-[174px] flex-col items-center justify-center rounded-md border-[#E5E7EB] p-7 text-center hover:cursor-pointer sm:border-solid sm:border-[#DDD] md:min-h-[252px] md:w-1/4 md:border-2 ${
              selected === PaymentMethodsEnum.CARD ? 'border-[#1A56DB] bg-[#1A56DB] text-[#FFFFFF] shadow-xl' : ''
            } ${props.getValues('investingProfile.amount') > 5250 ? 'opacity-50' : 'opacity-1'}`}
            onClick={() => {
              if (!isLoading && props.getValues('investingProfile.amount') <= 5250) {
                handlePaymentSelection(PaymentMethodsEnum.CARD)
              }
            }}
          >
            <img src={CreditDebitIcon} alt="CreditDebitIcon" className={` min-w-[92px]`} />
            <div className={`mt-4 text-base ${selected === PaymentMethodsEnum.CARD ? 'font-bold' : 'font-normal'}`}>
              Credit/Debit
            </div>
          </div>

          <div
            style={{ border: '2px solid #E5E7EB' }}
            className={`flex min-h-[174px] flex-col items-center justify-center rounded-md border-[#E5E7EB] p-7 text-center hover:cursor-pointer sm:border-solid sm:border-[#DDD] md:min-h-[252px] md:w-1/4 md:border-2 ${
              selected === PaymentMethodsEnum.WIRE ? 'border-[#1A56DB] bg-[#1A56DB] text-[#FFFFFF] shadow-xl' : ''
            }`}
            onClick={() => {
              if (!isLoading) {
                handlePaymentSelection(PaymentMethodsEnum.WIRE)
              }
            }}
          >
            <img src={BankWireIcon} alt="BankWireIcon" className="min-w-[92px]" />
            <div className={`mt-4 text-base ${selected === PaymentMethodsEnum.WIRE ? 'font-bold' : 'font-normal'}`}>
              Bank Wire
            </div>
          </div>

          {/* <div
            className={`flex flex-col justify-center border-2 text-center rounded-xl p-7 ${
              selected === 'checkByMail' ? 'border-indigo-500 shadow-xl' : ''
            }`}
            onClick={() => {
              handlePaymentSelection('checkByMail')
            }}
          >
            <img src={CheckByMailIcon} />
            <div className="mt-3">Check by Mail</div>
          </div> */}
        </div>
        {isLoading && Number(props.getValues('investingProfile.amount')) > 5250 && (
          <div className="mt-4 text-base leading-5 text-gray-500">Credit Card transactions are limited to $5250.00</div>
        )}
        <div className="w-full justify-center gap-3 pt-10 sm:block md:flex">
          <div className="flex flex-col sm:w-[100%] md:w-[20%]">
            <Button btnName={'Back'} onClickHandler={() => props.previousStep('7')} disabled={isLoading} />
          </div>
          <div className="flex flex-col sm:w-[100%] md:w-[80%]">
            <Button
              btnName={'Next: Process Payment'}
              btnType={'button'}
              onClickHandler={handleSubmit}
              disabled={isLoading}
            />
          </div>
        </div>
        {isLoading && (
          <div className="mt-4 flex gap-3 rounded-md bg-[#FFE5D3] p-4">
            <div className="flex items-center">
              <LuLoader2 className="animate-spin text-4xl" />
            </div>
            <div className="text-sm">
              Connecting to the payment processing screen may take up to 20 seconds. Thank you for your patience!
            </div>
          </div>
        )}
      </div>
      {/* <LoaderModal isVisible={isLoading} /> */}
      <ErrorMessageModal
        isVisible={error !== ''}
        onProceed={() => {
          setError('')
        }}
        onCancel={() => {}}
        error={error}
        btn2={'Ok'}
      />
      <MessageModal
        isVisible={existingCreditCard || errorMessage !== ''}
        onProceed={() => {
          // automatically set the step to appropriate form duplicate error
          if (errorMessage === RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.') {
            setErrorMessage('')
            props.nextStep('3')
            return
          }
          if (errorMessage === RiseUpErrors.DuplicateSSN + ' Please update the ssn.') {
            setErrorMessage('')
            props.nextStep('4')
            return
          }
          setExistingCreditCard(false)
          onProceed()
        }}
        onCancel={() => {
          setExistingCreditCard(false)
        }}
        title={errorMessage !== '' ? 'Duplicate data error' : 'Existing Credit Card'}
        description={
          errorMessage !== ''
            ? errorMessage
            : 'You already have added a credit card previously. Click Proceed to start initiating payment.'
        }
        type={errorMessage !== '' ? 'error' : 'success'}
        btn2={errorMessage !== '' ? 'Ok' : 'Proceed'}
      />
    </div>
  )
}

export default PaymentSelection
